export type PanLayoutMetrics = {
  imgWidth: number;
  imgHeight: number;
  layoutLeft: number;
  layoutTop: number;
  containerWidth: number;
  containerHeight: number;
};

export type PanOffset = {
  x: number;
  y: number;
};

export function parseZoomOriginPx(origin: string, imgWidth: number, imgHeight: number) {
  if (origin === "center center") {
    return { ox: imgWidth / 2, oy: imgHeight / 2 };
  }

  const match = origin.match(/^([\d.]+)px\s+([\d.]+)px$/);
  if (match) {
    return { ox: Number(match[1]), oy: Number(match[2]) };
  }

  return { ox: imgWidth / 2, oy: imgHeight / 2 };
}

export function clampPanOffset(
  offset: PanOffset,
  scale: number,
  zoomOrigin: string,
  layout: PanLayoutMetrics | null,
): PanOffset {
  if (scale <= 1 || !layout) {
    return { x: 0, y: 0 };
  }

  const {
    imgWidth,
    imgHeight,
    layoutLeft,
    layoutTop,
    containerWidth,
    containerHeight,
  } = layout;
  const { ox, oy } = parseZoomOriginPx(zoomOrigin, imgWidth, imgHeight);
  const originShiftX = ox * (1 - scale);
  const originShiftY = oy * (1 - scale);
  const visualWidth = imgWidth * scale;
  const visualHeight = imgHeight * scale;

  let x = offset.x;
  let y = offset.y;

  if (visualWidth <= containerWidth) {
    x = (containerWidth - visualWidth) / 2 - layoutLeft - originShiftX;
  } else {
    const minX = containerWidth - layoutLeft - originShiftX - visualWidth;
    const maxX = -layoutLeft - originShiftX;
    x = Math.min(maxX, Math.max(minX, x));
  }

  if (visualHeight <= containerHeight) {
    y = (containerHeight - visualHeight) / 2 - layoutTop - originShiftY;
  } else {
    const minY = containerHeight - layoutTop - originShiftY - visualHeight;
    const maxY = -layoutTop - originShiftY;
    y = Math.min(maxY, Math.max(minY, y));
  }

  return { x, y };
}

export function anchorWheelZoomToPoint({
  clientX,
  clientY,
  scale,
  offset,
  zoomOrigin,
  layout,
  containerLeft,
  containerTop,
}: {
  clientX: number;
  clientY: number;
  scale: number;
  offset: PanOffset;
  zoomOrigin: string;
  layout: PanLayoutMetrics;
  containerLeft: number;
  containerTop: number;
}): { origin: string; offset: PanOffset } {
  const cursorX = clientX - containerLeft;
  const cursorY = clientY - containerTop;

  if (scale <= 1) {
    const originX = cursorX - layout.layoutLeft;
    const originY = cursorY - layout.layoutTop;
    return {
      origin: `${originX}px ${originY}px`,
      offset: { x: 0, y: 0 },
    };
  }

  const { ox, oy } = parseZoomOriginPx(zoomOrigin, layout.imgWidth, layout.imgHeight);
  const imageX = (cursorX - layout.layoutLeft - offset.x - ox * (1 - scale)) / scale;
  const imageY = (cursorY - layout.layoutTop - offset.y - oy * (1 - scale)) / scale;

  return {
    origin: `${imageX}px ${imageY}px`,
    offset: {
      x: cursorX - layout.layoutLeft - imageX,
      y: cursorY - layout.layoutTop - imageY,
    },
  };
}

export function buildPhotoViewerUrl(photoIds: string[], currentPhotoId: string) {
  const params = new URLSearchParams();
  params.set("photo", currentPhotoId);
  const index = photoIds.findIndex((photoId) => photoId === currentPhotoId);
  if (index >= 0) {
    params.set("i", String(index));
  }
  if (photoIds.length > 1) {
    params.set("photos", JSON.stringify(photoIds));
  }
  return `/app/photo?${params.toString()}`;
}

export function getPhotoSwipeDirection({
  deltaX,
  deltaY,
  viewportWidth,
}: {
  deltaX: number;
  deltaY: number;
  viewportWidth: number;
}): "previous" | "next" | null {
  const horizontalDistance = Math.abs(deltaX);
  const verticalDistance = Math.abs(deltaY);
  const minimumDistance = Math.min(72, Math.max(44, viewportWidth * 0.12));

  if (horizontalDistance < minimumDistance || horizontalDistance <= verticalDistance * 1.2) {
    return null;
  }

  return deltaX < 0 ? "next" : "previous";
}

export function parsePhotoViewerParams(search: string) {
  const params = new URLSearchParams(search);
  const photoId = params.get("photo") || "";
  let photoIds: string[] = [];

  try {
    const raw = params.get("photos");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        photoIds = parsed.filter((photo): photo is string => typeof photo === "string" && photo.length > 0);
      }
    }
  } catch {
    photoIds = [];
  }

  if (!photoIds.length && photoId) {
    photoIds = [photoId];
  }

  const parsedIndex = Number(params.get("i"));
  const index = Number.isInteger(parsedIndex) && parsedIndex >= 0 && parsedIndex < photoIds.length
    ? parsedIndex
    : Math.max(0, photoIds.findIndex((photo) => photo === photoId));

  return {
    photoId: photoIds[index] || photoId,
    photoIds,
    index: index >= 0 ? index : 0,
  };
}
